/**
 * `Navbar` — barra di navigazione principale dell'applicazione.
 *
 * Mostra il brand, i link di navigazione filtrati per ruolo (con evidenziazione
 * della rotta attiva via `NavLink`) e il controllo di sessione (`UserMenu`).
 * È responsive: sotto una certa larghezza i link collassano dietro un toggle.
 *
 * I link condizionati al ruolo replicano la logica delle guardie di rotta
 * (difensiva lato client; l'autorizzazione autorevole resta l'API):
 *  - sezione Assessment (`/assessment/*`): sempre (sessione autenticata);
 *  - Review (`/review`): reviewer|manager|admin;
 *  - Insights (`/insights`, ex "Livelli"): admin o `levelsEnabled`;
 *  - area admin (`/admin/*`, nel `UserMenu`): admin (e manager per skill/competenze).
 *
 * Il ruolo proviene da `useAuth()` (`/api/me` autorevole, con fallback ai claim
 * OIDC finché il profilo non è caricato).
 */
import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { UserMenu } from './UserMenu';
import { SectionSwitcher } from './SectionSwitcher';
import { sectionForPath, type SectionAccess } from './sections';

export function Navbar() {
  const { role, me } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  // Contesto di accesso per il selettore di sezione: replica (in modo difensivo)
  // le guardie di rotta. La sezione Insights (ex "Livelli") è accessibile
  // all'admin o agli utenti con `levelsEnabled`; l'API resta autorevole (403).
  const access: SectionAccess = {
    role,
    levelsEnabled: role === 'admin' || me?.levelsEnabled === true,
    cvEnabled: role === 'admin' || me?.cvEnabled === true,
  };

  // Chiude il menu mobile a ogni cambio di rotta.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // La home (hub) è il punto di scelta della sezione: lì non mostriamo né il
  // selettore né i link di sezione (altrimenti `sectionForPath('/')` cadrebbe
  // sul default Assessment e ne esporrebbe il sotto-menu).
  const isHome = location.pathname === '/';

  // Le voci mostrate dipendono dalla sezione attiva (derivata dal path): il
  // `SectionSwitcher` cambia sezione, questi sono i link interni alla sezione.
  // Le voci di amministrazione NON stanno qui: sono nel menu utente
  // (UserMenu), visibili ad admin/manager. Vedi components/UserMenu.tsx.
  const activeSection = sectionForPath(location.pathname);
  const items = isHome ? [] : activeSection.items;

  // Le voci della sezione Insights si distinguono per query string (`?view=`)
  // sullo stesso path `/insights`: `NavLink` confronta solo il pathname, quindi
  // le evidenziamo confrontando anche la query. `view` assente ⇒ vista "Team".
  const currentView = new URLSearchParams(location.search).get('view');
  const isItemActive = (item: (typeof items)[number], navLinkActive: boolean) => {
    if (!item.to.startsWith('/insights')) return navLinkActive;
    const itemView = new URL(item.to, window.location.origin).searchParams.get(
      'view',
    );
    // Attiva solo se siamo su /insights e la vista coincide (entrambe null ⇒ Team).
    return location.pathname === '/insights' && itemView === currentView;
  };

  return (
    <header className="navbar">
      <div className="navbar__inner">
        <Link to="/" className="navbar__brand">
          <img className="navbar__logo" src="/imgs/logo.png" alt="Skills" />
        </Link>

        {/* Sezione corrente + relative sotto pagine raggruppate in un'unica
            "pill" visiva: il selettore (con la sezione attiva) e i link interni
            condividono lo stesso contenitore, separati da un chevron `>`. Sulla
            home (hub) il gruppo è superfluo: la pagina stessa è il punto di
            scelta della sezione. */}
        {!isHome && (
          <div className="navbar__section-group">
            <SectionSwitcher pathname={location.pathname} access={access} />

            <nav
              id="primary-navigation"
              aria-label="Navigazione principale"
              className={['navbar__links', menuOpen ? 'is-open' : '']
                .filter(Boolean)
                .join(' ')}
            >
              {items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    ['navbar__link', isItemActive(item, isActive) ? 'is-active' : '']
                      .filter(Boolean)
                      .join(' ')
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>
        )}

        <span className="navbar__spacer" />

        <div className="navbar__actions">
          <UserMenu />
          <button
            type="button"
            className="ui-btn ui-btn--ghost navbar__toggle"
            aria-expanded={menuOpen}
            aria-controls="primary-navigation"
            aria-label="Apri o chiudi il menu di navigazione"
            onClick={() => setMenuOpen((v) => !v)}
          >
            <span aria-hidden="true">{menuOpen ? '✕' : '☰'}</span>
          </button>
        </div>
      </div>
    </header>
  );
}
